import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl-next';
import EditorExpandIcon from '@atlaskit/icon/core/migration/grow-horizontal--editor-expand';
import EditorCollapseIcon from '@atlaskit/icon/core/migration/shrink-horizontal--editor-collapse';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { messages } from './messages';
import { Blur, ButtonIcon, ToggleButton } from './styled';
var EXPANSION_STATE = /*#__PURE__*/function (EXPANSION_STATE) {
  EXPANSION_STATE[EXPANSION_STATE["LOADING"] = 1] = "LOADING";
  // still computing if the threshold is crossed
  EXPANSION_STATE[EXPANSION_STATE["COLLAPSED"] = 2] = "COLLAPSED";
  // threshold crossed, render component collapsed
  EXPANSION_STATE[EXPANSION_STATE["EXPANDED"] = 3] = "EXPANDED"; // below threshold, render exanded without collapse option
  return EXPANSION_STATE;
}(EXPANSION_STATE || {});
var containerStyles = xcss({
  overflow: 'hidden',
  position: 'relative'
});
var expandedStyles = xcss({
  height: 'auto'
});
var collapsedStyles = xcss({
  height: '150px'
});
var pointerCursorStyles = xcss({
  cursor: 'pointer'
});
var titleContainer = xcss({
  color: 'color.text.subtlest',
  font: 'font.body.UNSAFE_small',
  fontWeight: 'font.weight.semibold',
  paddingTop: 'space.200',
  paddingRight: 'space.300',
  paddingBottom: 'space.0',
  paddingLeft: 'space.300'
});
var titleStyles = xcss({
  alignContent: 'center',
  height: '26px'
});
export var Collapse = function Collapse(_ref) {
  var children = _ref.children,
    collapsed = _ref.collapsed,
    isLoading = _ref.isLoading,
    _ref$toggleCollapsedS = _ref.toggleCollapsedState,
    toggleCollapsedState = _ref$toggleCollapsedS === void 0 ? function () {} : _ref$toggleCollapsedS,
    _ref$expandWhenClicke = _ref.expandWhenClickedAnywhere,
    expandWhenClickedAnywhere = _ref$expandWhenClicke === void 0 ? false : _ref$expandWhenClicke,
    _ref$isStreaming = _ref.isStreaming,
    isStreaming = _ref$isStreaming === void 0 ? false : _ref$isStreaming,
    _ref$disableCollapse = _ref.disableCollapse,
    disableCollapse = _ref$disableCollapse === void 0 ? false : _ref$disableCollapse,
    callbacks = _ref.callbacks;
  var _useState = useState(EXPANSION_STATE.LOADING),
    _useState2 = _slicedToArray(_useState, 2),
    initialExpansionState = _useState2[0],
    setInitialExpansionState = _useState2[1];
  var threshold = 150;
  var collapseableContainerRef = useCallback(function (node) {
    if (!node || disableCollapse) {
      return;
    }
    var resizeObserver = new ResizeObserver(debounce(function () {
      // delay computation to ensure the observed node has rendered and the height reported is accurate
      setTimeout(function () {
        var childHeight = node.getBoundingClientRect().height;
        if (isStreaming || isLoading || !Boolean(childHeight)) {
          return;
        }

        // checking if the length of the answer is smaller than the collapse threshold
        if (childHeight <= threshold && initialExpansionState !== EXPANSION_STATE.EXPANDED) {
          setInitialExpansionState(EXPANSION_STATE.EXPANDED);
        } else {
          setInitialExpansionState(EXPANSION_STATE.COLLAPSED);
        }
      }, 100);
    }, 100));
    resizeObserver.observe(node);
  },
  // make sure the dependancy array is empty,
  // this is to ensure the resizeObserver is registered just once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useEffect(function () {
    /**
     * if the answer has finished isLoading and the container is too small to collapse,
     * toggle collapsed state to false
     * this helps external components watching the boolean to know the actual state
     */
    if (initialExpansionState === EXPANSION_STATE.EXPANDED && !isLoading && !isStreaming && collapsed || disableCollapse) {
      var _callbacks$onExpanded;
      toggleCollapsedState();
      callbacks === null || callbacks === void 0 || (_callbacks$onExpanded = callbacks.onExpandedByDefault) === null || _callbacks$onExpanded === void 0 || _callbacks$onExpanded.call(callbacks);
    }
  }, [initialExpansionState, isLoading, isStreaming, collapsed, callbacks, toggleCollapsedState, disableCollapse]);
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var tooltipText = formatMessage(collapsed ? messages.ai_answer_expand_tooltip : messages.ai_answer_collapse_tooltip);

  // allow whole container clicks to expand when enabled
  var onContainerClickHandler = function onContainerClickHandler() {
    if (expandWhenClickedAnywhere && collapsed) {
      var _callbacks$onExpandCl;
      callbacks === null || callbacks === void 0 || (_callbacks$onExpandCl = callbacks.onExpandClicked) === null || _callbacks$onExpandCl === void 0 || _callbacks$onExpandCl.call(callbacks);
      toggleCollapsedState();
    }
  };
  var onClickHandler = function onClickHandler(e) {
    // stop container click from being called
    e.stopPropagation();
    if (collapsed) {
      var _callbacks$onExpandCl2;
      callbacks === null || callbacks === void 0 || (_callbacks$onExpandCl2 = callbacks.onExpandClicked) === null || _callbacks$onExpandCl2 === void 0 || _callbacks$onExpandCl2.call(callbacks);
    } else {
      var _callbacks$onCollapse;
      callbacks === null || callbacks === void 0 || (_callbacks$onCollapse = callbacks.onCollapseClicked) === null || _callbacks$onCollapse === void 0 || _callbacks$onCollapse.call(callbacks);
    }
    toggleCollapsedState();
  };

  /**
   * render the non collapsable version
   * - child container's height is lesser than the threshold
   * - collapsed card is externally disabled, ex: for a person card
   */
  if (initialExpansionState === EXPANSION_STATE.EXPANDED || disableCollapse) {
    return /*#__PURE__*/React.createElement(Box, {
      xcss: [containerStyles, expandedStyles]
    }, /*#__PURE__*/React.createElement("div", null, !isLoading && /*#__PURE__*/React.createElement(Inline, {
      alignBlock: "center",
      xcss: titleContainer
    }, /*#__PURE__*/React.createElement(Box, {
      xcss: titleStyles
    }, formatMessage(messages.title_label))), children));
  }
  return (
    /*#__PURE__*/
    // eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
    React.createElement(Box, {
      xcss: [containerStyles, collapsed ? collapsedStyles : expandedStyles, expandWhenClickedAnywhere && collapsed && pointerCursorStyles],
      onClick: onContainerClickHandler
    }, /*#__PURE__*/React.createElement(Inline, {
      alignBlock: "center",
      spread: 'space-between',
      xcss: titleContainer
    }, /*#__PURE__*/React.createElement(Box, {
      xcss: titleStyles
    }, formatMessage(messages.title_label)), initialExpansionState === EXPANSION_STATE.COLLAPSED && /*#__PURE__*/React.createElement(Tooltip, {
      position: 'bottom',
      content: tooltipText
    }, /*#__PURE__*/React.createElement(ToggleButton, {
      onClick: onClickHandler,
      "data-testid": "expand-collapse-toggle"
    }, /*#__PURE__*/React.createElement(ButtonIcon, null, collapsed ? /*#__PURE__*/React.createElement(EditorExpandIcon, {
      color: "currentColor",
      spacing: "spacious",
      label: formatMessage(messages.expand_label)
    }) : /*#__PURE__*/React.createElement(EditorCollapseIcon, {
      color: "currentColor",
      spacing: "spacious",
      label: formatMessage(messages.collapse_label)
    }))))), /*#__PURE__*/React.createElement("div", {
      ref: collapseableContainerRef
    }, children), initialExpansionState === EXPANSION_STATE.COLLAPSED && collapsed && /*#__PURE__*/React.createElement(Blur, null))
  );
};